import { ReactNode } from "react";
import { Layout } from "react-grid-layout";
import { useTranslation } from "react-i18next";
import DataTable from "../../components/table/data/DataTable";
import { ERole } from "../../models/enums/role";
import DonutChart from "../../pages/dashboard/chart/global/DonutChart";
import HorizontalBarChart from "../../pages/dashboard/chart/global/HorizontalBarChart";
import StackedBarChart from "../../pages/dashboard/chart/global/StackedBarChart";
import StatCard from "../../pages/dashboard/chart/global/StatCard";
import VerticalBarChart from "../../pages/dashboard/chart/global/VerticalBarChart";
import auth from "../../services/auth/AuthService";
import DownloadTable from "../../utils/download/downloadDataTable";
import { useMiscleanedLocalsColumns, useTimesheetOverviewColumns } from "../useTableColumns";
import useGlobalDashboardData from "./data/useGlobalDashboardData";

interface IWidgetConfig {
  title: string;
  component: ReactNode;
  layout: Layout;
  downloadButtons?: { label: string; onClick: () => void }[];
}

interface IUseWidgetsProps {
  propertyGroupId: number;
  start: Date;
  end: Date;
}

const useWidgets = ({ propertyGroupId, start, end }: IUseWidgetsProps) => {
  const { t } = useTranslation();
  const nonVisitedLocals = useMiscleanedLocalsColumns({ hasTimeColumns: false });
  const insufficientlyCleanedLocals = useMiscleanedLocalsColumns({ hasTimeColumns: true });
  const timesheetOverviewColumns = useTimesheetOverviewColumns();

  const { isLoading, data } = useGlobalDashboardData({ propertyGroupId, start, end });
  const userRole = auth.user?.role;
  const isLimitedClient = userRole === ERole.LimitedClient;


  const downloadNonVisitedLocals = new DownloadTable(t("globalDashboard.titlelistNonVisitedLocals"), data.miscleanedLocalsDisplay.nonVisitedLocals, nonVisitedLocals, t("globalDashboard.titlelistNonVisitedLocals"));

  const downloadInsufficientlyCleanedLocals = new DownloadTable(t("globalDashboard.titlelistInsufficientlyCleanedLocals"), data.miscleanedLocalsDisplay.insufficientlyCleanedLocals, insufficientlyCleanedLocals, t("globalDashboard.titlelistInsufficientlyCleanedLocals"));


  const widgets: IWidgetConfig[] = [
    {
      title: t("globalDashboard.globalTime"),
      component: <StatCard text={`${data.globalHours} ${t("globalDashboard.hours")}`} percentageChange={data.globalPercentageChange} isPositiveValueBeneficial={true} />,
      layout: { i: "globalTime", x: 0, y: 0, w: 2, h: 1 },
    },
    {
      title: t("globalDashboard.workedTime"),
      component: <StatCard text={`${data.workedHours} ${t("globalDashboard.hours")}`} percentageChange={data.workedPercentageChange} isPositiveValueBeneficial={true} />,
      layout: { i: "workedTime", x: 2, y: 0, w: 2, h: 1 },
    },

    {
      title: t("globalDashboard.workedTimeByBuilding"),
      component: <StackedBarChart series={data.workedHoursByBuilding.series} categories={data.workedHoursByBuilding.categories} />,
      layout: { i: "workedTimeByBuilding", x: 0, y: 1, w: 4, h: 4 },
    },
    {
      title: t("globalDashboard.globalTimeByDay"),
      component: <VerticalBarChart data={data.globalHoursByDay.data} categories={data.globalHoursByDay.categories} seriesName={t("globalDashboard.hours")} />,
      layout: { i: "globalTimeByDay", x: 4, y: 1, w: 2, h: 4 },
    },
    {
      title: t("globalDashboard.workedTimeRatioByBuilding"),
      component: <DonutChart labels={data.workedHoursRatioByBuilding.labels} series={data.workedHoursRatioByBuilding.series} />,
      layout: { i: "workedTimeRatioByBuilding", x: 0, y: 5, w: 2, h: 4 },
    },
    {
      title: t("globalDashboard.listEmployees"),
      component: <DataTable
        height="400px"
        disableFiltrer={true}
        rows={data.employeesTimesheetOverview}
        columns={timesheetOverviewColumns}
        rowsPerPageOptions={[5]}
        hasBorder={false}
      />,
      layout: { i: "listEmployees", x: 4, y: 5, w: 4, h: 4 },

    },
    {
      title: t("globalDashboard.nonVisitedLocals"),
      component: <StatCard text={`${data.miscleanedLocals.nonVisitedLocals.length} ${t("globalDashboard.locals")}`} percentageChange={data.miscleanedLocalsPercentageChange.nonVisitedLocals} isPositiveValueBeneficial={false} />,
      layout: { i: "nonVisitedLocals", x: 0, y: 6, w: 2, h: 1 },

    },
    {
      title: t("globalDashboard.insufficientlyCleanedLocals"),
      component: <StatCard text={`${data.miscleanedLocals.insufficientlyCleanedLocals.length} ${t("globalDashboard.locals")}`} percentageChange={data.miscleanedLocalsPercentageChange.insufficientlyCleanedLocals} isPositiveValueBeneficial={false} />,
      layout: { i: "insufficientlyCleanedLocals", x: 2, y: 6, w: 2, h: 1 },
    },
    {
      title: t("globalDashboard.overCleanedLocals"),
      component: <StatCard text={`${data.miscleanedLocals.overCleanedLocals.length} ${t("globalDashboard.locals")}`} percentageChange={data.miscleanedLocalsPercentageChange.overCleanedLocals} isPositiveValueBeneficial={false} />,
      layout: { i: "overCleanedLocals", x: 4, y: 6, w: 2, h: 1 },
    },
    {
      title: t("globalDashboard.listNonVisitedLocals"),
      component: <DataTable
        height="400px"
        disableFiltrer={true}
        rows={data.miscleanedLocalsDisplay.nonVisitedLocals}
        columns={nonVisitedLocals}
        rowsPerPageOptions={[5]}
        hasBorder={false}
      />,
      layout: { i: "listNonVisitedLocals", x: 0, y: 7, w: 4, h: 4 },
      downloadButtons: [
        { label: "PDF", onClick: () => downloadNonVisitedLocals.GetPDF(t("header.exportedBy"), t("header.exportDate")) },
        { label: "Excel", onClick: () => downloadNonVisitedLocals.GetExcel(t("header.exportedBy"), t("header.exportDate")) },

      ],
    },
    {
      title: t("globalDashboard.workedTimeRatioByProfile"),
      component: <DonutChart labels={data.workedHoursRatioByProfile.labels} series={data.workedHoursRatioByProfile.series} />,
      layout: { i: "workedTimeRatioByProfile", x: 4, y: 7, w: 2, h: 4 },
    },
    {
      title: t("globalDashboard.listInsufficientlyCleanedLocals"),
      component: <DataTable
        height="400px"
        disableFiltrer={true}
        rows={data.miscleanedLocalsDisplay.insufficientlyCleanedLocals}
        columns={insufficientlyCleanedLocals}
        rowsPerPageOptions={[5]}
        hasBorder={false}
      />,
      layout: { i: "listInsufficientlyCleanedLocals", x: 4, y: 11, w: 4, h: 4 },
      downloadButtons: [
        { label: "PDF", onClick: () => downloadInsufficientlyCleanedLocals.GetPDF(t("header.exportedBy"), t("header.exportDate")) },
        { label: "Excel", onClick: () => downloadInsufficientlyCleanedLocals.GetExcel(t("header.exportedBy"), t("header.exportDate")) },

      ],
    },
    {
      title: t("globalDashboard.top10FloorsByWorkedHours"),
      component: <HorizontalBarChart data={data.top10FloorsByWorkedHours.data} categories={data.top10FloorsByWorkedHours.categories} seriesName={t("globalDashboard.hours")} />,
      layout: { i: "top10FloorsByWorkedHours", x: 0, y: 11, w: 2, h: 4 },
    },
  ];


  if (!isLimitedClient) {
    widgets.push({
      title: t("globalDashboard.productivity"),
      component: <StatCard text={data.productivity !== undefined ? `${data.productivity}%` : "- - -"} percentageChange={data.productivityPercentageChange} isPositiveValueBeneficial={true} />,
      layout: { i: "productivity", x: 4, y: 0, w: 2, h: 1 },
    });
  }


  return { widgets, isLoading };
}

export default useWidgets;
